import React, { useState } from 'react';
import PhoneLogin from './PhoneLogin';
import VerifyCode from './VerifyCode';
import Signup from './Signup';
import { apiClient } from '../../lib/apiClient';
import { sendPhoneVerificationCode } from '../../lib/firebase';

function Auth({ onAuthSuccess }) {
  const [step, setStep] = useState('login'); // 'login', 'verify', 'signup'
  const [phoneNumber, setPhoneNumber] = useState(''); // For display (with +91)
  const [phoneNumberWithoutCode, setPhoneNumberWithoutCode] = useState(''); // For API (without +91)
  const [sessionInfo, setSessionInfo] = useState('');
  const [firebaseUid, setFirebaseUid] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (phone) => {
    setLoading(true);
    try {
      // Use Firebase SDK to send verification code (handles reCAPTCHA internally)
      const result = await sendPhoneVerificationCode(phone);
      
      // Only proceed if we have sessionInfo - don't navigate on failure
      if (!result || !result.sessionInfo) {
        throw new Error('Failed to receive session info from Firebase');
      }
      
      // Only update state and navigate if we have valid sessionInfo
      setPhoneNumber(phone); // Store formatted phone with +91 for display
      setSessionInfo(result.sessionInfo);
      setStep('verify');
    } catch (error) {
      // Don't navigate - stay on login page
      // Re-throw error so PhoneLogin can display it
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (session, code) => {
    setLoading(true);
    try {
      const response = await apiClient.verifyCode(session, code);
      
      if (response.userExists) {
        // User exists, login successful
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        onAuthSuccess(response.user, response.token);
      } else {
        // User doesn't exist, go to signup
        // Store phone number without code and firebaseUid from response
        setPhoneNumberWithoutCode(response.phoneNumber); // Phone without +91
        setFirebaseUid(response.firebaseUid);
        setStep('signup');
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (phone, name) => {
    setLoading(true);
    try {
      // Use phoneNumberWithoutCode (without +91) and firebaseUid
      const response = await apiClient.signup(phoneNumberWithoutCode, name, firebaseUid);
      localStorage.setItem('auth_token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      onAuthSuccess(response.user, response.token);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    // Resend code using the same phone number
    await handleSendCode(phoneNumber);
  };

  if (step === 'login') {
    return <PhoneLogin onSendCode={handleSendCode} loading={loading} />;
  }

  if (step === 'verify') {
    return (
      <VerifyCode
        phoneNumber={phoneNumber}
        sessionInfo={sessionInfo}
        onVerifyCode={handleVerifyCode}
        onResendCode={handleResendCode}
        loading={loading}
      />
    );
  }

  if (step === 'signup') {
    return (
      <Signup
        phoneNumber={phoneNumber}
        onSignup={handleSignup}
        loading={loading}
      />
    );
  }

  return null;
}

export default Auth;

